import React, { useRef, useMemo, useState } from "react"
import { Formik, Form } from "formik"
import {
  Button,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress,
} from "@mui/material"
import makeStyles from "@mui/styles/makeStyles"
import clsx from "clsx"
import Select from "react-select"
import Autocomplete from "@mui/material/Autocomplete"
import moment from "moment"
import { getSupplierItemsBySupplier } from "../../parseManager/suppliers/supplierItems/parseSupplierItemManager"
import { getLotsForLotInventory } from "../../parseManager/lot/parseLotManager"
import { COLORS } from "../../utils"

const initialValues = {
  suppliers: [],
  supplierItemOptions: [],
  supplierItem: null,
  lotOptions: [],
  lot: null
}

const formatSupplierItemAutocompleteOptions = (supplierItemOptions) =>
  supplierItemOptions.map((supplierItem) => ({
    label: supplierItem.name + " - " + supplierItem.objectId,
    value: supplierItem.objectId,
  }))

const formatLotAutocompleteOptions = (lotOptions) =>
  lotOptions.map((lot) => ({
    label: lot.lotNumber + " (" + moment.utc(lot.dlc).startOf("day").format("DD/MM/YYYY") + ")",
    value: lot.objectId,
    data: lot
  }))

const useStyles = makeStyles((theme) => ({
  dialog: {
    width: 641,
    height: 422,
  },
  formControl: {
    marginLeft: "unset",
    width: "100%",
    marginTop: 20,
    "& *": {
      fontSize: 16,
      color: COLORS.BLACK,
    },
    "& .MuiFormLabel-root": {
      position: "relative",
      height: 20,
      display: "flex",
      alignItems: "center",
      transform: "none",
      marginTop: 4,
    },
  },
  selectFormControl: {
    composes: "$formControl",
    marginTop: 16,
  },
  dialogTitle: {
    paddingBottom: 0,
  },
  dialogDescription: {
    color: COLORS.DARK_BLACK,
  },
  plusIconContainer: {
    composes: "flexCenter",
    backgroundColor: theme.palette.primary.main,
    width: 32,
    height: 32,
    borderRadius: 3,
  },
  plusIcon: {
    color: COLORS.WHITE,
  },
  addButton: {
    textTransform: "capitalize",
    fontSize: 16,
    letterSpacing: 0.15,
    fontWeight: 500,
    marginTop: 18,
    padding: 0,
  },
  chips: {
    padding: theme.spacing(0.5),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
}))

const selectStyles = {
  control: (styles) => ({
    ...styles,
    backgroundColor: "transparent",
    border: "none",
    borderBottom: `1px solid ${COLORS.GREY_SEARCH}`,
    borderRadius: "none",
    overflowY: "scroll",
  }),
}

const LoadingIndicator = () => <CircularProgress color="inherit" size={20} />

export const DisplayLotsToInventoryModal = (props) => {
  const { onClose, open, suppliers, stockZone, loading, onConfirm } = props

  const formikRef = useRef(null)
  const [loadingItems, setLoadingItems] = useState(false)
  const [loadingLots, setLoadingLots] = useState(false)

  const classes = useStyles()

  const supplierOptions = useMemo(() => 
    suppliers
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((supplier) => ({
        value: supplier.objectId,
        label: supplier.name,
      })),
    [suppliers]
  )

  const handleSuppliersChange = async (options, setFieldValue) => {
    setLoadingItems(true)
    setFieldValue("suppliers", options)
    setFieldValue("supplierItem", null)
    setFieldValue("lot", null)

    let allSupplierItems = []
    for(const option of options){
      const supplierPointer = {
        __type: "Pointer",
        className: "Suppliers",
        objectId: option.value
      }
      const supplierItems = await getSupplierItemsBySupplier(supplierPointer)
      allSupplierItems = [...allSupplierItems, ...supplierItems]
    }
    
    setFieldValue("supplierItemOptions", allSupplierItems)
    setLoadingItems(false)
  }

  const handleSupplierItemChange = async (value, setFieldValue) => {
    setLoadingLots(true)
    setFieldValue("lot", null)
    setFieldValue("supplierItem", value)

    if (value) {
      const lotOptions = await getLotsForLotInventory(value.value, stockZone.objectId)
      setFieldValue("lotOptions", lotOptions)
    }

    setLoadingLots(false)
  }

  const handleConfirm = () => {
    formikRef.current.submitForm()
  }

  const handleSubmit = (values) => {
    // supplierItem and lot is required to confirm the dialog
    if (!(values.supplierItem && values.lot)) return
    onConfirm(values.lot)
    onClose()
  }

  const handleCancel = () => {
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="form-dialog-title"
      classes={{ paper: classes.dialog }}
      sx={{ "& .MuiDialog-paper": {height: "426px"} }}
    >
      <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
        Afficher de nouveau un lot dans l’inventaire
      </DialogTitle>
      <DialogContent>
        <DialogContentText className={classes.dialogDescription}>
          Sélectionnez un lot qui n’est plus présent sur l’interface inventaire  et que vous souhaitez afficher de nouveau.
        </DialogContentText>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {({ values, handleBlur, setFieldValue }) => {
            return (
              <Form>
                <FormControl
                  className={clsx(
                    classes.formControl,
                    classes.multiSelectFormControl
                  )}
                  variant="standard"
                >
                  <InputLabel>Fournisseurs</InputLabel>
                  <Select
                    name="suppliers"
                    options={supplierOptions}
                    closeMenuOnSelect={false}
                    isMulti
                    styles={selectStyles}
                    value={values.suppliers}
                    isLoading={loading}
                    onChange={(option) => {handleSuppliersChange(option, setFieldValue)}}
                    components={{ LoadingIndicator }}
                  />
                </FormControl>
                <FormControl
                  className={classes.selectFormControl}
                  variant="standard"
                >
                  <Autocomplete
                    id="supplierItem"
                    name="supplierItem"
                    options={formatSupplierItemAutocompleteOptions(values.supplierItemOptions || [])}
                    getOptionLabel={(option) => option.label || ""}
                    value={values.supplierItem || null}
                    onChange={(_, value) => handleSupplierItemChange(value, setFieldValue)}
                    isOptionEqualToValue={(option, value) =>
                      option.objectId === value.objectId
                    }
                    disabled={(values.suppliers || []).length === 0}
                    renderInput={(params) => (
                      <TextField
                        variant="standard"
                        {...params}
                        onBlur={handleBlur}
                        label="Nom de l'article"
                        value={values.supplierItem ? values.supplierItem.value : ""}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {loadingItems ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                  />
                </FormControl>
                <FormControl
                  className={classes.selectFormControl}
                  variant="standard"
                >
                  <Autocomplete
                    id="lot"
                    name="lot"
                    options={formatLotAutocompleteOptions(values.lotOptions || [])}
                    getOptionLabel={(option) => option?.label || ""}
                    onChange={(_, value) => setFieldValue("lot", value)}
                    value={values.lot || null}
                    isOptionEqualToValue={(option, value) =>
                      option.objectId === value.objectId
                    }
                    disabled={values.supplierItem === null}
                    renderInput={(params) => (
                      <TextField
                        variant="standard"
                        {...params}
                        onBlur={handleBlur}
                        label="Numéro de lot (DLC)"
                        disabled={values.supplierItem === null}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                            {loadingLots ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                          ),
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Form>
            )
          }}
        </Formik>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          Annuler
        </Button>
        <Button onClick={handleConfirm} color="primary" variant="contained">
          Afficher
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DisplayLotsToInventoryModal
